import React, { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';

// Admin Operations (#9): manual triggers for existing backend jobs. Each action is
// confirmed before firing and is audit-logged server-side. These can be slow (they
// run real jobs) and may incur AWS/Bedrock cost — hence the explicit confirmations.

const OPS = [
  { key: 'digest', label: 'Run Daily Digest', path: '/admin/ops/digest',
    desc: 'Builds + emails the daily digest now (matches alerts, runs AI, sends email). Slow.',
    danger: true },
  { key: 'weekly-digest', label: 'Run Weekly Digest', path: '/admin/ops/weekly-digest',
    desc: 'Sends the weekly AI-picks digest to opted-in members (ships dark unless enabled).',
    danger: true },
  { key: 'watchlist-alerts', label: 'Run Watchlist Alerts', path: '/admin/ops/watchlist-alerts',
    desc: 'Evaluates watchlist alerts and emails matches to members.', danger: true },
  { key: 'global-ai-rank', label: 'Trigger Global AI Rank', path: '/admin/ops/global-ai-rank',
    desc: 'Scores the stock universe with Bedrock (non-blocking background job). Costs ~$20-35 for a full run.',
    danger: true, body: { force: false } },
  { key: 'ai-backfill', label: 'AI Analysis Backfill (8)', path: '/admin/ops/ai-backfill',
    desc: 'Backfills AI analysis for recent signals (one small page of 8).',
    body: { limitPerType: 8, offset: 0 } },
];

const AdminOps = ({ embedded = false }) => {
  const [busy, setBusy] = useState(null);
  const [rankStatus, setRankStatus] = useState(null);

  const loadRankStatus = useCallback(async () => {
    try {
      const res = await axios.get('/admin/ops/global-ai-rank/status');
      setRankStatus(res.data);
    } catch (_) { /* ignore */ }
  }, []);

  useEffect(() => { loadRankStatus(); }, [loadRankStatus]);

  const run = async (op) => {
    const ok = window.confirm(`Run "${op.label}"?\n\n${op.desc}\n\nThis is a real action and is audit-logged.`);
    if (!ok) return;
    setBusy(op.key);
    try {
      const res = await axios.post(op.path, op.body || {});
      toast.success(`${op.label}: ${res.data?.ok ? 'started/completed' : 'done'}`);
      if (op.key === 'global-ai-rank') loadRankStatus();
    } catch (e) {
      toast.error(e.response?.data?.message || `${op.label} failed`);
    } finally {
      setBusy(null);
    }
  };

  const inner = (
    <>
      {!embedded && <h1 className="text-2xl font-bold text-gray-900 mb-1">Operations</h1>}
      <p className="text-sm text-gray-500 mb-4">Manual job triggers — confirmed &amp; audit-logged.</p>

      {rankStatus && (
        <div className="bg-white rounded-lg shadow-sm p-4 mb-4 text-sm">
          <div className="font-semibold text-gray-900 mb-1">Global AI Rank status</div>
          <div className="flex flex-wrap gap-4 text-gray-600">
            <span>Enabled: <b>{String(rankStatus.enabled)}</b></span>
            <span>Running: <b>{String(rankStatus.running)}</b></span>
            <span>In window: <b>{String(rankStatus.inWindow)}</b></span>
            <span>Completed this month: <b>{String(rankStatus.completedThisMonth)}</b></span>
            <span>Scored this month: <b>{rankStatus.scoredThisMonth ?? '—'}</b></span>
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-3">
        {OPS.map((op) => (
          <div key={op.key} className="bg-white rounded-lg shadow-sm p-4 flex flex-col justify-between">
            <div>
              <div className="font-semibold text-gray-900">{op.label}</div>
              <p className="text-sm text-gray-500 mt-1">{op.desc}</p>
            </div>
            <button
              onClick={() => run(op)}
              disabled={busy === op.key}
              className={`mt-3 self-start px-4 py-1.5 text-sm rounded font-medium disabled:opacity-50 ${
                op.danger ? 'bg-amber-600 hover:bg-amber-700 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white'
              }`}
            >
              {busy === op.key ? 'Running…' : 'Run'}
            </button>
          </div>
        ))}
      </div>
      <p className="text-xs text-gray-400 mt-3">
        These invoke live backend jobs; some send real emails or incur AWS cost. Every run is recorded in the Audit Log.
      </p>
    </>
  );

  if (embedded) return inner;
  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-5xl mx-auto px-4">{inner}</div>
    </div>
  );
};

export default AdminOps;
